import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';

import {
  DnsValidationCancelResponse,
  DnsValidationStatusResponse,
  DnsValidationVerifyResponse,
  DomainValidationState,
} from './dns-validation.models';
import { COMPANY_API_BASE_URL } from './company-api.tokens';

/**
 * `/api/dnsvalidation/*` — the domain-ownership proof behind the company upgrade.
 *
 * Shared by both portals; the API base comes from {@link COMPANY_API_BASE_URL} so neither app's
 * config service leaks into the library. Error behaviour is preserved verbatim from the main
 * portal's original service: the domain-state probe degrades to a `REQUEST_FAILED` result (the
 * caller renders a message), while verify/cancel rethrow so the caller can map the error code.
 */
@Injectable({
  providedIn: 'root',
})
export class DnsValidationService {
  private http = inject(HttpClient);
  private readonly apiUrl = inject(COMPANY_API_BASE_URL);

  getValidationRecordName(domain: string): string {
    return domain;
  }

  getDomainValidationState(domain: string): Observable<DomainValidationState> {
    return this.http
      .post<DomainValidationState>(`${this.apiUrl}/dnsvalidation/domain-state`, { domain })
      .pipe(
        catchError((error) => {
          console.error('Error checking domain validation state', error);
          return of({
            isValidationRequired: false,
            canProceed: false,
            errorCode: 'REQUEST_FAILED',
          });
        })
      );
  }

  // Single verify method for both manual and auto verification
  verifyValidation(domain: string): Observable<DnsValidationVerifyResponse> {
    return this.http
      .post<DnsValidationVerifyResponse>(`${this.apiUrl}/dnsvalidation/verify`, { domain })
      .pipe(
        catchError((error) => {
          console.error('Error verifying DNS validation', error);
          throw error;
        })
      );
  }

  getCurrentValidation(): Observable<DnsValidationStatusResponse | null> {
    return this.http
      .get<DnsValidationStatusResponse>(`${this.apiUrl}/dnsvalidation/current`)
      .pipe(
        map((response) => (response.isValidationRequired ? response : null)),
        catchError((error) => {
          console.error('Error getting current DNS validation', error);
          return of(null);
        })
      );
  }

  cancelValidation(domain: string): Observable<DnsValidationCancelResponse> {
    return this.http
      .delete<DnsValidationCancelResponse>(
        `${this.apiUrl}/dnsvalidation/cancel/${encodeURIComponent(domain)}`
      )
      .pipe(
        catchError((error) => {
          console.error('Error cancelling DNS validation', error);
          // Error structure is { error: { code: string, details?: string, data?: any } }
          throw error;
        })
      );
  }
}
